import * as Immutable from "immutable";

import { shallowEqual } from "./memo";

export function listConcat<V extends Immutable.List<any>>(list1: V, list2: V) {
    return list1.concat(list2) as V;
}

export function listCount<V>(list: Immutable.List<V>, condition: (value: V) => boolean) {
    return list.count(condition);
}

export function listFilter<V>(list: Immutable.List<V>, condition: (value: V) => boolean) {
    return list.filter(condition) as Immutable.List<V>;
}

export function listFilterUndefined<V>(list: Immutable.List<V | undefined>) {
    return list.filter((v: V | undefined) => v !== undefined) as Immutable.List<V>;
}

export function listFindIndex<V>(list: Immutable.List<V>, condition: (value: V) => boolean) {
    return list.findIndex(condition);
}

export function listFindLastIndex<V>(list: Immutable.List<V>, condition: (value: V) => boolean) {
    return list.findLastIndex(condition);
}

export function listFirst<V>(list: Immutable.List<V>): V | undefined {
    return list.first<undefined>();
}

// ORIGINAL TYPE IS:
// export interface Iterable<K, V> {
//   map<M>(
//     mapper: (value?: V, key?: K, iter?: /*this*/Iterable<K, V>) => M,
//     context?: any
//   ): /*this*/Iterable<K, M>;
export function listMap<V, M>(list: Immutable.List<V>, mapper: (value: V, key: number) => M) {
    // this wrapper just makes value of type `V` instead of `V | undefined`
    return list.map(mapper) as Immutable.List<M>;
}

export function listReduce<V, R>(list: Immutable.List<V>, reducer: (reduction: R, value: V, key: number) => R, initialReduction: R) {
    return list.reduce(reducer, initialReduction);
}

export function listTakeLast<V>(list: Immutable.List<V>, amount: number) {
    return list.takeLast(amount) as Immutable.List<V>;
}

export function mapFind<K, V>(map: Immutable.Map<K, V>, condition: (value: V, key: K) => boolean) {
    return map.find(condition) as V | undefined;
}

export function mapFilter<K, V>(map: Immutable.Map<K, V>, condition: (value: V, key: K) => boolean) {
    return map.filter(condition) as Immutable.Map<K, V>;
}

export function mapFilterSubtype<K, V, T extends V>(map: Immutable.Map<K, V>, condition: (value: V, key: K) => value is T) {
    return map.filter(condition) as Immutable.Map<K, T>;
}

export function mapForEach<K, V>(map: Immutable.Map<K, V>, sideEffect: (value: V, key: K) => void) {
    return map.forEach(sideEffect);
}

export function mapDelete<K, V>(map: Immutable.Map<K, V>, key: K) {
    return map.delete(key);
}

// ORIGINAL TYPE IS:
// export interface Map<K, V> extends Collection.Keyed<K, V> {
//   update(key: K, notSetValue: V, updater: (value: V) => V): Map<K, V>;
export function mapUpdate<K, V, NV>(
    map: Immutable.Map<K, V>,
    key: K,
    notSetValue: NV,
    updater: (value: V | NV) => V | undefined
): Immutable.Map<K, V> {
    // updater can return undefined to remove value from map
    return map.update(key, notSetValue as any, updater as any);
}

export function mapUpdateV<K, V>(map: Immutable.Map<K, V>, key: K, notSetValue: V, updater: (value: V) => V | undefined): Immutable.Map<K, V> {
    return mapUpdate(map, key, notSetValue, updater);
}

export function mapMap<K, V, M>(map: Immutable.Map<K, V>, mapper: (value: V, key: K) => M) {
    return map.map(mapper) as Immutable.Map<K, M>;
}

export function mapReduce<K, V, R>(map: Immutable.Map<K, V>, reducer: (reduction: R, value: V, key: K) => R, initialReduction: R) {
    return map.reduce(reducer, initialReduction) as R;
}
export function mapUpdateExisting<K, V>(map: Immutable.Map<K, V>, key: K, updater: (value: V) => V) {
    return map.update(key, x => x !== undefined ? updater(x) : x);
}

export function orderedMapFilter<K, V>(map: Immutable.OrderedMap<K, V>, condition: (value: V) => boolean) {
    return map.filter(condition) as Immutable.OrderedMap<K, V>;
}
export function orderedMapFilterUndefined<K, V>(map: Immutable.OrderedMap<K, V | undefined>) {
    return map.filter((v: V | undefined) => v !== undefined) as Immutable.OrderedMap<K, V>;
}
export function orderedMapForEach<K, V>(map: Immutable.OrderedMap<K, V>, sideEffect: (value: V, key: K) => void) {
    return map.forEach(sideEffect);
}

export const orderedMapHas = <K, V>(map: Immutable.OrderedMap<K, V>, key: K) => {
    return map.has(key);
}

export function orderedMapMap<K, V, M>(map: Immutable.OrderedMap<K, V>, mapper: (value: V, key: K) => M) {
    return map.map(mapper) as Immutable.OrderedMap<K, M>;
}
export function orderedMapMapEntries<K, V, KM, VM>(map: Immutable.OrderedMap<K, V>, mapper: (entry: [K, V]) => [KM, VM]) {
    return map.mapEntries(mapper) as Immutable.OrderedMap<KM, VM>;
}
export function orderedMapReduce<K, V, R>(map: Immutable.OrderedMap<K, V>, reducer: (reduction: R, value: V, key: K) => R, initialReduction: R) {
    return map.reduce(reducer, initialReduction);
}
export function orderedMapSort<K, V>(map: Immutable.OrderedMap<K, V>, comparator: (valueA: V, valueB: V) => number) {
    return map.sort(comparator) as Immutable.OrderedMap<K, V>;
}
export function orderedMapUpdate<K, V>(map: Immutable.OrderedMap<K, V>, key: K, notSetValue: V, updater: (value: V) => V) {
    return map.update(key, notSetValue, updater);
}
export function orderedMapUpdateExisting<K, V>(map: Immutable.OrderedMap<K, V>, key: K, updater: (value: V) => V) {
    return map.update(key, x => x !== undefined ? updater(x) : x);
}

// ordered set
export function orderedSetConcat<V1, V2>(set1: Immutable.OrderedSet<V1>, set2: Immutable.OrderedSet<V2>): Immutable.OrderedSet<V1 | V2> {
    return set1.concat(set2) as any;
}
export function orderedSetDifference<V>(set1: Immutable.OrderedSet<V>, set2: Immutable.OrderedSet<V>) {
    return orderedSetFilter(set1, (s) => !set2.has(s));
}
export function orderedSetFilter<V>(set: Immutable.OrderedSet<V>, condition: (value: V) => boolean) {
    return set.filter(condition) as Immutable.OrderedSet<V>;
}
export function orderedSetFind<V>(set: Immutable.OrderedSet<V>, condition: (value: V) => boolean): V | undefined {
    return set.find(condition);
}
export function orderedSetMap<V, M>(set: Immutable.OrderedSet<V>, mapper: (value: V) => M): Immutable.OrderedSet<M> {
    return set.map(mapper) as any;
}

export function setConcat<V1, V2>(set1: Immutable.Set<V1>, ...set2: Immutable.Set<V2>[]): Immutable.Set<V1 | V2> {
    return set1.concat(...set2) as any;
}

// other
export function is<I>(first: I, second: I) {
    return Immutable.is(first, second);
}

export const sameSet = <T>() => {
    let cache = Immutable.Set<T>();
    return (set: Immutable.Set<T>) => {
        if (set === cache || set.equals(cache)) {
            return cache // return object equal by reference if set is equal
        } else {
            return cache = set;
        }
    };
};

export const shallowSameMap = <K, V>() => {
    let cache = Immutable.Map<K, V>();
    return (map: Immutable.Map<K, V>) => {
        if (
            map === cache ||
            map.equals(cache) ||
            (map.size === cache.size &&
                map.entrySeq().every(([key, value]) => {
                    return shallowEqual(cache.get(key), value);
                }))
        ) {
            return cache; // return object equal by reference if map is shallowly equal
        } else {
            return (cache = map);
        }
    };
};

export const shallowSameOrderedMap = <K, V>() => {
    let cache = Immutable.OrderedMap<K, V>();
    return (map: Immutable.OrderedMap<K, V>) => {
        let i = 0;
        if (
            map === cache ||
            map.equals(cache) ||
            (map.size === cache.size &&
                map.entrySeq().every(([key, value]) => {
                    const index = i++;
                    let j = 0;
                    const [cacheKey, cacheValue] = cache.findEntry(() => j++ === index)! // ts! because of equal size
                    return cacheKey === key && shallowEqual(cacheValue, value);
                }))
        ) {
            return cache; // return object equal by reference if map is shallowly equal
        } else {
            return (cache = map);
        }
    };
};

export const shallowSameList = <V>() => {
    let cache = Immutable.List<V>();
    return (list: Immutable.List<V>) => {
        if (
            list === cache ||
            list.equals(cache) ||
            (list.size === cache.size &&
                list.entrySeq().every(([key, value]) => {
                    return shallowEqual(cache.get(key), value);
                }))
        ) {
            return cache; // return object equal by reference if map is shallowly equal
        } else {
            return (cache = list);
        }
    };
};
